type SfxName = 'launch' | 'arrived' | 'reply' | 'tap';

const PAD_NOTES = [65.41, 98.0, 164.81, 261.63];

export class SoundBoard {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private muted = false;
  private bgmStarted = false;
  private bgmGain: GainNode | null = null;
  private bgmOscs: OscillatorNode[] = [];

  setMuted(b: boolean): void {
    this.muted = b;
    if (this.masterGain) this.masterGain.gain.value = b ? 0 : 0.55;
    if (this.bgmGain) this.bgmGain.gain.value = b ? 0 : 0.16;
    if (this.ctx) {
      if (b) {
        this.ctx.suspend().catch(() => {});
      } else {
        this.ctx.resume().catch(() => {});
      }
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  private ensureCtx(): AudioContext | null {
    if (this.muted) return null;
    if (this.ctx) return this.ctx;
    type AudioCtor = typeof AudioContext;
    const W = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
    const Ctor = W.AudioContext ?? W.webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.55;
    this.masterGain.connect(this.ctx.destination);
    return this.ctx;
  }

  play(name: SfxName): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    switch (name) {
      case 'launch':
        this.synthWhoosh(ctx, this.masterGain);
        break;
      case 'arrived':
        this.synthChime(ctx, this.masterGain, 660, 880);
        break;
      case 'reply':
        this.synthChime(ctx, this.masterGain, 520, 780);
        break;
      case 'tap':
        this.synthBlip(ctx, this.masterGain, 440);
        break;
    }
  }

  private synthWhoosh(ctx: AudioContext, dest: GainNode): void {
    const now = ctx.currentTime;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.4;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, now);
    filter.frequency.exponentialRampToValueAtTime(220, now + 0.45);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(0.55, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    src.connect(filter).connect(gain).connect(dest);
    src.start(now);
    src.stop(now + 0.55);
  }

  private synthChime(ctx: AudioContext, dest: GainNode, f1: number, f2: number): void {
    const now = ctx.currentTime;
    const make = (freq: number, delay: number, dur: number): void => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now + delay);
      gain.gain.exponentialRampToValueAtTime(0.45, now + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + dur);
      osc.connect(gain).connect(dest);
      osc.start(now + delay);
      osc.stop(now + delay + dur + 0.02);
    };
    make(f1, 0, 0.45);
    make(f2, 0.12, 0.5);
  }

  private synthBlip(ctx: AudioContext, dest: GainNode, freq: number): void {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.3, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(gain).connect(dest);
    osc.start(now);
    osc.stop(now + 0.2);
  }

  startBgmOnFirstInteraction(): void {
    if (this.bgmStarted) return;
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    this.bgmStarted = true;

    const bgmGain = ctx.createGain();
    bgmGain.gain.value = this.muted ? 0 : 0.16;
    bgmGain.connect(this.masterGain);
    this.bgmGain = bgmGain;

    const now = ctx.currentTime;
    PAD_NOTES.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = i === 0 ? 'sine' : 'triangle';
      osc.frequency.value = freq;
      const noteGain = ctx.createGain();
      noteGain.gain.value = 0;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.04 + i * 0.013;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.5;
      const offset = ctx.createConstantSource();
      offset.offset.value = 0.5;
      lfo.connect(lfoGain);
      offset.connect(noteGain.gain);
      lfoGain.connect(noteGain.gain);
      osc.connect(noteGain).connect(bgmGain);
      lfo.start(now + i * 0.7);
      offset.start(now);
      osc.start(now + i * 0.4);
      this.bgmOscs.push(osc);
    });
  }
}
